import { createContext, useEffect, useReducer, useContext } from "react";
import type { Dispatch, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import authReducer, { initialState } from "state/reducers/authReducer";
import type { ActionInterface, AuthStateInterface } from "state/reducers/types";
import type { User } from "@app-types/tap-partner-vault";

type SessionResponse = {
  authenticated: boolean;
  user: User | null;
  expiresAt: string | null;
};

const SESSION_ENDPOINT = "/api/auth/session";

const fetchSession = async (): Promise<SessionResponse> => {
  const response = await fetch(SESSION_ENDPOINT, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to load auth session");
  }

  return response.json() as Promise<SessionResponse>;
};

interface AuthContextProps {
  state: AuthStateInterface;
  dispatch: Dispatch<ActionInterface>;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextProps>({
  state: initialState,
  dispatch: () => undefined,
});

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    let cancelled = false;

    const bootstrapAuth = async () => {
      dispatch({ type: "AUTH_LOADING" });

      try {
        const session = await fetchSession();

        if (cancelled) {
          return;
        }

        if (session.authenticated && session.user) {
          dispatch({
            type: "AUTHENTICATED",
            payload: { user: session.user },
          });

          if (window.location.pathname === "/login") {
            navigate("/", { replace: true });
          }
          return;
        }

        dispatch({ type: "UNAUTHENTICATED" });
      } catch {
        if (cancelled) {
          return;
        }

        dispatch({
          type: "UNAUTHENTICATED",
          payload: {
            error: "Sign on failed. Please try again.",
          },
        });

        if (window.location.pathname !== "/login") {
          navigate("/login?error=auth_failed", { replace: true });
        }
      }
    };

    bootstrapAuth();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <AuthContext.Provider value={{ state, dispatch }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
